import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  Box,
  IconButton,
  Divider,
  Chip,
  Button,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useMusicPlayer } from '../../contexts/MusicPlayerContext';
import { getImageUrl, shouldUseCrossOrigin } from '../../constants/routes';


const PlayHistory = ({ open, onClose }) => {
  const { playHistory, playTrack, clearPlayHistory, getRecentTracks } = useMusicPlayer();
  const [showAll, setShowAll] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const recentTracks = showAll ? playHistory : getRecentTracks(10);

  const formatTimeAgo = (timestamp) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  const handlePlayTrack = (track) => {
    if (track.playlist && track.playlistIndex !== undefined) {
      // Restore the original playlist and play from the same position
      playTrack(track.playlistIndex);
    } else {
      // Play as individual track
      playTrack(track);
    }
    onClose();
  };

  const handleClearHistory = () => {
    clearPlayHistory();
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          backgroundColor: 'background.paper',
          borderRadius: isMobile ? 0 : 2,
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon color="primary" />
          <Typography variant="h6">Play History</Typography>
          <Chip 
            label={playHistory.length} 
            size="small" 
            color="primary" 
            variant="outlined"
          />
        </Box>
        <Box>
          {playHistory.length > 0 && (
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleClearHistory}
              sx={{ mr: 1 }}
            >
              Clear
            </Button>
          )}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {playHistory.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            py: 4,
            color: 'text.secondary'
          }}>
            <HistoryIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
            <Typography variant="body1" sx={{ mb: 1 }}>
              No play history yet
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'center' }}>
              Start playing music to see your listening history here
            </Typography>
          </Box>
        ) : (
          <>
            <List sx={{ p: 0 }}>
              {recentTracks.map((track, index) => (
                <React.Fragment key={`${track.id}-${track.playedAt}`}>
                  <ListItem
                    sx={{
                      py: 1.5,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                      cursor: 'pointer'
                    }}
                    onClick={() => handlePlayTrack(track)}
                  >
                    <ListItemAvatar sx={{ mr: 2 }}>
                      <Box
                        component="img"
                        src={track.image ? getImageUrl(track.image) : '/default-album-art.jpg'}
                        alt={track.name}
                        crossOrigin={track.image && shouldUseCrossOrigin(getImageUrl(track.image)) ? "anonymous" : undefined}
                        sx={{ 
                          width: 48, 
                          height: 48,
                          borderRadius: 0,
                          objectFit: 'cover'
                        }}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography 
                          variant="subtitle1" 
                          sx={{ 
                            fontWeight: 'medium',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            mb: 0.5
                          }}
                        >
                          {track.name}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              mb: 0.5
                            }}
                          >
                            {track.act_name || track.acts}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PlayArrowIcon 
                              sx={{ 
                                fontSize: 16, 
                                opacity: 0.7,
                                '&:hover': { opacity: 1 }
                              }} 
                            />
                            <Typography variant="caption" color="text.disabled">
                              {formatTimeAgo(track.playedAt)}
                              {track.playlist && (
                                <span> • From playlist</span>
                              )}
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < recentTracks.length - 1 && (
                    <Divider sx={{ opacity: 0.1 }} />
                  )}
                </React.Fragment>
              ))}
            </List>

            {playHistory.length > 10 && (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={() => setShowAll(!showAll)}
                  size="small"
                >
                  {showAll ? 'Show Less' : `Show All ${playHistory.length} Tracks`}
                </Button>
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PlayHistory;
